import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import secureLocalStorage from 'react-secure-storage';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Grid, Divider, Backdrop, Container, Typography } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import Loader from 'src/components/loaders/Loader';
import DashboardToolbar from 'src/components/ToolBars/DashboardToolbar';

import { useAuth } from '../../hooks/useAuth';
import { fetchStatuses } from '../../services/status.service';
import { fetchTicketsByDate } from '../../services/ticket.service';
import { getUsersByDepartments } from '../../services/user.service';
import { fetchDepartmentsByIds } from '../../services/department.service';
// Import scrollbar styles
// Import icons
import { PieChart, BarChart, AppWidgetSummary } from '../../components/charts';
import DepartmentSelect from '../../components/departmentSelect/DepartmentSelect';

// ----------------------------------------------------------------------

export default function DepartmentManagerDashboard() {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { username, user: authUser, departmentIds } = useAuth();

  const [departments, setDepartments] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [statusesList, setStatusesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(0);

  const start = useMemo(() => new Date(secureLocalStorage.getItem('ticketStart')), []);
  const end = useMemo(() => new Date(secureLocalStorage.getItem('ticketEnd')), []);

  const fetchAssignedDepartments = useCallback(async () => {
    if (!departmentIds?.length) return;
    genericApiToaster(
      fetchDepartmentsByIds,
      [departmentIds],
      '',
      '',
      null,
      null,
      t,
      setDepartments
    );
  }, [departmentIds, t]);

  const getStatuses = useCallback(
    async (ids) => {
      if (!ids?.length) return;
      genericApiToaster(fetchStatuses, [ids], '', '', null, null, t, (res) => {
        setStatusesList(
          res.filter((s) => ids.includes(s.departmentId) || s.departmentId === 0)
        );
      });
    },
    [t]
  );

  const fetchAgentsData = useCallback(async () => {
    if (!departmentIds?.length) return;
    const agentsRes = await getUsersByDepartments(departmentIds);
    setAllAgents(agentsRes.filter((user) => !user.archived));
  }, [departmentIds]);

  const getTicketsByDate = useCallback(
    async ({ startDate, endDate, attributeName }) => {
      setIsLoading(true);
      await genericApiToaster(
        fetchTicketsByDate,
        { startDate, endDate, attributeName },
        '',
        '',
        null,
        null,
        t,
        (res) => setAllTickets(res),
        undefined,
        () => setIsLoading(false)
      );
    },
    [t]
  );

  useEffect(() => {
    if (departmentIds?.length > 0) {
      getStatuses(departmentIds);
      fetchAssignedDepartments();
      fetchAgentsData();
    }
  }, [departmentIds, getStatuses, fetchAssignedDepartments, fetchAgentsData]);

  useEffect(() => {
    if (statusesList.length > 0) {
      getTicketsByDate({ startDate: start, endDate: end, attributeName: 'created' });
    }
  }, [statusesList, getTicketsByDate, start, end]);

  const metrics = useMemo(() => {
    const statusMap = Object.fromEntries(statusesList.map((s) => [s.statusId, s]));

    // 1. Filter Tickets based on dropdown
    const filteredTickets = allTickets
      .filter(
        (t) =>
          selectedDepartmentId === 0 ||
          Number(t.departmentId) === Number(selectedDepartmentId)
      )
      .map((ticket) => ({
        ...ticket,
        statusObject: statusMap[ticket.status_id] || {
          name: 'Unknown',
          systemMappedStatus: 'OPEN',
        },
      }));

    // 2. Filter Agents based on dropdown
    const filteredAgents = allAgents.filter(
      (a) =>
        selectedDepartmentId === 0 ||
        (a.departmentIds || []).includes(Number(selectedDepartmentId))
    );

    // 3. Calculate Counts
    const counts = { open: 0, onhold: 0, resolved: 0, closed: 0, unassigned: 0 };
    const sCounts = {};
    const aMap = {};

    filteredTickets.forEach((ticket) => {
      const { statusObject, agentName, assignedTo } = ticket;
      const status = statusObject?.systemMappedStatus?.toLowerCase();
      if (status) counts[status] = (counts[status] || 0) + 1;

      const sName = statusObject?.name;
      if (sName) sCounts[sName] = (sCounts[sName] || 0) + 1;

      if (agentName && agentName !== 'none') {
        if (!aMap[assignedTo])
          aMap[assignedTo] = { agent: agentName, agentId: assignedTo, tickets: [] };
        aMap[assignedTo].tickets.push(ticket);
      }
    });

    return {
      tickets: filteredTickets,
      agents: filteredAgents,
      ticketCount: counts,
      statusCounts: sCounts,
      ticketsByAgent: Object.values(aMap),
    };
  }, [allTickets, allAgents, selectedDepartmentId, statusesList]);

  return (
    <>
      <Helmet>
        <title> Oryx One Ticketing System </title>
      </Helmet>
      <Container maxWidth="xl">
        <Backdrop
          sx={{
            bgcolor: 'rgb(121 121 121 / 32%)',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
          open={isLoading}
        >
          <Loader size={isSmallScreen ? 80 : 175} hideText />
        </Backdrop>

        <Box sx={{ mb: 5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 900,
              fontSize: '0.65rem',
              letterSpacing: '0.1em',
            }}
          >
            {t('dashboard.greeting').toUpperCase()}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {authUser?.givenName || username || ''}!
          </Typography>
          <Divider
            sx={{ mt: 2, borderStyle: 'solid', borderColor: 'rgba(0,0,0,0.08)' }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            alignItems: 'center',
            width: '100%',
            mb: 4,
          }}
        >
          <DashboardToolbar
            getTicketsByDate={getTicketsByDate}
            start={start}
            end={end}
            setIsLoading={setIsLoading}
          />
          <Box sx={{ mt: isSmallScreen ? 2 : 0, ml: !isSmallScreen ? 2 : 0 }}>
            <DepartmentSelect
              options={departments}
              setSelectedDepId={setSelectedDepartmentId}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3} height={isSmallScreen ? 'auto' : '100%'}>
              {/* Column 2 (Right): 2x2 AppWidgetSummary Grid */}
              <Grid item xs={12} sm={6} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6}>
                    <AppWidgetSummary
                      title={t('dashboard.totalTickets')}
                      total={metrics.tickets.length}
                      color="warning"
                      icon="ph:ticket"
                      sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <AppWidgetSummary
                      title={t('dashboard.unassignedTickets')}
                      total={metrics.ticketCount.unassigned}
                      color="error"
                      icon="system-uicons:ticket"
                      sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <AppWidgetSummary
                      title={t('dashboard.activeUsers')}
                      total={metrics.agents.length}
                      color="info"
                      icon="mdi:face-agent"
                      sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <AppWidgetSummary
                      title={t('dashboard.closureRate')}
                      total={
                        (metrics.ticketCount.resolved / (metrics.tickets.length || 1)) *
                        100
                      }
                      icon="material-symbols:percent"
                      sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <BarChart
                title={t('dashboard.bestPerAgents')}
                subheader={departments.map((d) => d.name).join(', ')}
                chartData={metrics.ticketsByAgent
                  .filter((a) =>
                    a.tickets.some(
                      (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                    )
                  )
                  .sort(
                    (a, b) =>
                      b.tickets.filter(
                        (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                      ).length -
                      a.tickets.filter(
                        (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                      ).length
                  )
                  .map((a) => ({
                    label: a.agent,
                    value: a.tickets.filter(
                      (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                    ).length,
                  }))}
              />
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <PieChart
                title={t('dashboard.ticketByStatus')}
                chartData={statusesList.map((s) => ({
                  label: s.statusId < 6 ? t(`status.${s.name}`) : s.name,
                  value: metrics.statusCounts[s.name] || 0,
                }))}
                chartColors={statusesList.map((s) => {
                  const colors = {
                    1: '#d32f2f',
                    2: '#4caf50',
                    3: '#9c27b0',
                    4: '#ed6c02',
                    5: '#1976d2',
                  };
                  return colors[s.statusId] || s.color;
                })}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
